import React, { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Shield,
    Settings,
    LogOut,
    ChevronRight,
    CreditCard,
    UserCircle,
    Bell,
    HelpCircle,
    Home,
    Building,
    Mail,
    Phone,
    User
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const Sidebar = ({ onClose }) => {
    const location = useLocation();
    const { logout } = useAuth();
    const [hoveredItem, setHoveredItem] = useState(null);
    const [userData, setUserData] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);

    // Cargar datos del usuario desde localStorage al montar
    useEffect(() => {
        const loadUserData = () => {
            try {
                const userStr = localStorage.getItem('user');
                if (userStr) {
                    const user = JSON.parse(userStr);
                    setUserData(user);
                    
                    // Verificar si el usuario es administrador
                    // Dependiendo de cómo esté estructurado el objeto user, ajusta estas condiciones
                    const userRole = user.rol?.toLowerCase();
                    const isAdminUser = userRole === 'admin' || 
                                       userRole === 'administrador' || 
                                       userRole === 'administracion' ||
                                       userRole === 'administrator';
                    setIsAdmin(isAdminUser);
                }
            } catch (error) {
                console.error('Error al cargar datos del usuario:', error);
            }
        };

        loadUserData();

        // Escuchar cambios en localStorage
        const handleStorageChange = (e) => {
            if (e.key === 'user') {
                loadUserData();
            }
        };

        window.addEventListener('storage', handleStorageChange);

        // También escuchar cambios desde la misma pestaña
        const intervalId = setInterval(loadUserData, 5000);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
            clearInterval(intervalId);
        };
    }, []);

    // Memoizar navegación principal
    const mainNavigation = useMemo(() => [
        {
            name: 'Préstamos',
            href: '/admin/Security',
            icon: CreditCard,
            description: 'Gestión de préstamos'
        },
        {
            name: 'Configuración',
            href: '/admin/settings',
            icon: Settings,
            description: 'Ajustes del sistema'
        }
    ], []);

    // Navegación solo para administradores
    const adminNavigation = useMemo(() => [
        {
            name: 'Usuarios',
            href: '/admin/users',
            icon: User,
            description: 'Gestión de usuarios',
            adminOnly: true
        }
    ], []);

    // Combinar navegación basada en el rol
    const navigation = useMemo(() => {
        if (isAdmin) {
            return [...mainNavigation, ...adminNavigation];
        }
        return mainNavigation;
    }, [isAdmin, mainNavigation, adminNavigation]);

    const handleNavigation = () => {
        if (onClose && window.innerWidth < 1024) {
            onClose();
        }
    };

    const handleLogout = () => {
        if (onClose && window.innerWidth < 1024) {
            onClose();
        }
        logout();
    };

    const containerVariants = {
        hidden: { opacity: 0, x: -20 },
        visible: {
            opacity: 1,
            x: 0,
            transition: {
                duration: 0.3,
                staggerChildren: 0.05
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, x: -8 },
        visible: {
            opacity: 1,
            x: 0,
            transition: { duration: 0.2 }
        }
    };

    // Función para obtener iniciales del nombre
    const getInitials = (nombre, apellido) => {
        if (!nombre || !apellido) return 'U';
        return `${nombre.charAt(0)}${apellido.charAt(0)}`.toUpperCase();
    };

    return (
        <motion.div
            initial="hidden"
            animate="visible"
            variants={containerVariants}
            className="flex flex-col w-64 bg-white text-gray-800 relative overflow-hidden shadow-lg border-r border-gray-200"
        >
            <div className="relative z-10 flex flex-col h-full">
                {/* Sección de información del usuario */}
                <motion.div
                    variants={itemVariants}
                    className="p-4 border-b border-gray-100 bg-gradient-to-br from-gray-50 to-white"
                >
                    <div className="flex flex-col items-center">
                        {/* Avatar del usuario */}
                        <div className="relative mb-3">
                            {userData?.logo_empresa ? (
                                <motion.div
                                    whileHover={{ scale: 1.05 }}
                                    className="w-16 h-16 rounded-full overflow-hidden border-4 border-white shadow-lg"
                                >
                                    <img
                                        src={userData.logo_empresa}
                                        alt={userData.nombre_empresa || 'Logo empresa'}
                                        className="w-full h-full object-cover"
                                        onError={(e) => {
                                            e.target.onerror = null;
                                            e.target.src = `https://ui-avatars.com/api/?name=${getInitials(userData.nombre, userData.apellido)}&background=072146&color=fff&size=128`;
                                        }}
                                    />
                                </motion.div>
                            ) : (
                                <motion.div
                                    whileHover={{ scale: 1.05 }}
                                    className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center text-white font-bold text-xl shadow-lg border-4 border-white"
                                >
                                    {getInitials(userData?.nombre, userData?.apellido)}
                                </motion.div>
                            )}

                            {/* Indicador de rol */}
                            <div className={`absolute bottom-1 right-1 w-4 h-4 rounded-full border-2 border-white ${isAdmin ? 'bg-red-500' : 'bg-green-500'}`}></div>
                        </div>

                        {/* Información del usuario */}
                        <div className="text-center">
                            <h2 className="font-bold text-gray-900 text-sm">
                                {userData ? `${userData.nombre} ${userData.apellido}` : 'Cargando...'}
                            </h2>

                            {/* Mostrar rol del usuario */}
                            {userData?.rol && (
                                <p className={`text-xs font-medium mt-1 px-2 py-1 rounded-full ${isAdmin ? 'bg-red-100 text-red-700' : 'bg-blue-50 text-blue-600'}`}>
                                    {isAdmin ? 'Administrador' : userData.rol}
                                </p>
                            )}

                            {userData?.posicion && (
                                <p className="text-xs text-gray-600 mt-1">
                                    {userData.posicion}
                                </p>
                            )}

                            {userData?.nombre_empresa && (
                                <div className="flex items-center justify-center mt-2 text-gray-600">
                                    <Building className="w-3 h-3 mr-1" />
                                    <p className="text-xs truncate max-w-[180px]">
                                        {userData.nombre_empresa}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </motion.div>

                {/* Contenedor principal */}
                <div className="flex-1 flex flex-col min-h-0">
                    <div className="flex-1 overflow-y-auto custom-scrollbar">
                        {/* Navegación principal */}
                        <nav className="p-4 space-y-1">
                            <motion.div
                                variants={itemVariants}
                                className="text-xs font-medium text-gray-500 uppercase tracking-wider px-3 py-2"
                            >
                                Navegación
                            </motion.div>

                            <AnimatePresence>
                                {navigation.map((item) => {
                                    const Icon = item.icon;
                                    const isActive = location.pathname === item.href;
                                    const isHovered = hoveredItem === item.name;

                                    return (
                                        <motion.div
                                            key={item.name}
                                            variants={itemVariants}
                                            onHoverStart={() => setHoveredItem(item.name)}
                                            onHoverEnd={() => setHoveredItem(null)}
                                        >
                                            <Link
                                                to={item.href}
                                                onClick={handleNavigation}
                                                className="relative block"
                                            >
                                                <motion.div
                                                    className={`flex items-center px-3 py-2.5 rounded-lg transition-all duration-150 ${isActive
                                                        ? 'bg-blue-50 text-blue-700 border border-blue-100'
                                                        : 'text-gray-700 hover:bg-gray-50'
                                                        } ${item.adminOnly ? 'border-l-4 border-l-red-500' : ''}`}
                                                    whileHover={{ x: 2 }}
                                                >
                                                    {/* Indicador lateral sutil para item activo */}
                                                    {isActive && (
                                                        <motion.div
                                                            layoutId="activeIndicator"
                                                            className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-blue-600 rounded-r"
                                                        />
                                                    )}

                                                    {/* Icono */}
                                                    <div
                                                        className={`w-8 h-8 rounded-lg flex items-center justify-center mr-3 ${isActive
                                                            ? item.adminOnly ? 'bg-red-100' : 'bg-blue-100'
                                                            : item.adminOnly ? 'bg-red-50' : 'bg-gray-100'
                                                            }`}
                                                    >
                                                        <Icon className={`w-4 h-4 ${isActive
                                                            ? item.adminOnly ? 'text-red-600' : 'text-blue-600'
                                                            : item.adminOnly ? 'text-red-500' : 'text-gray-600'
                                                            }`} />
                                                    </div>

                                                    {/* Texto y descripción */}
                                                    <div className="flex-1">
                                                        <span className="font-medium text-sm block">{item.name}</span>
                                                        <span className="text-xs text-gray-500">
                                                            {item.description}
                                                        </span>
                                                    </div>

                                                    {/* Indicador de admin */}
                                                    {item.adminOnly && (
                                                        <div className="mr-2">
                                                            <Shield className="w-4 h-4 text-red-400" />
                                                        </div>
                                                    )}

                                                    {/* Flecha sutil al hacer hover */}
                                                    <AnimatePresence>
                                                        {isHovered && !isActive && (
                                                            <motion.div
                                                                initial={{ opacity: 0, x: -5 }}
                                                                animate={{ opacity: 1, x: 0 }}
                                                                exit={{ opacity: 0, x: -5 }}
                                                            >
                                                                <ChevronRight className="w-4 h-4 text-gray-400" />
                                                            </motion.div>
                                                        )}
                                                    </AnimatePresence>
                                                </motion.div>
                                            </Link>
                                        </motion.div>
                                    );
                                })}
                            </AnimatePresence>
                        </nav>
                    </div>
                </div>

                {/* Footer con cerrar sesión */}
                <motion.div
                    variants={itemVariants}
                    className="p-4 border-t border-gray-100 bg-gray-50"
                >
                    <motion.button
                        onClick={handleLogout}
                        whileHover={{ x: 2 }}
                        className="w-full flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:border-red-200 hover:bg-red-50 transition-all duration-150 group"
                    >
                        <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center group-hover:bg-red-100">
                                <LogOut className="w-4 h-4 text-gray-600 group-hover:text-red-600" />
                            </div>
                            <div className="text-left">
                                <p className="text-sm font-medium text-gray-900 group-hover:text-red-700">
                                    Cerrar Sesión
                                </p>
                                <p className="text-xs text-gray-500 group-hover:text-red-500">
                                    Salir del sistema
                                </p>
                            </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-red-400" />
                    </motion.button>

                    {/* Copyright con información de la empresa */}
                    <div className="mt-3 text-center">
                        <p className="text-xs text-gray-400 mb-1">
                            {userData?.nombre_empresa ? `© 2025 ${userData.nombre_empresa}` : '© 2025 Visioner7'}
                        </p>
                        <p className="text-[10px] text-gray-300">
                            {isAdmin ? 'Modo Administrador' : 'Plataforma BBVA'} - Versión 1.0
                        </p>
                    </div>
                </motion.div>
            </div>

            {/* Estilos personalizados para scrollbar */}
            <style>{`
                .custom-scrollbar {
                    scrollbar-width: thin;
                    scrollbar-color: rgba(156, 163, 175, 0.3) transparent;
                }
                .custom-scrollbar::-webkit-scrollbar {
                    width: 4px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: transparent;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: rgba(156, 163, 175, 0.3);
                    border-radius: 4px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: rgba(156, 163, 175, 0.5);
                }
            `}</style>
        </motion.div>
    );
};

export default Sidebar;